import type { MemberRole, MemberRoleMap } from './constants';

// Roles within a product are hierarchical (IDFE-4045): each assignable role's
// permissions are a superset of every assignable role listed after it, in the
// order the role definitions return them (e.g. Workspace: Admin ⊇ Viewer).
// Unassignable held roles sit outside the hierarchy; they're locked anyway.

function getAssignableRoleValues(roleOptions: readonly MemberRoleMap[]): MemberRole[] {
  return roleOptions.filter(({ assignable }) => assignable !== false).map(({ value }) => value);
}

/** Assignable roles ranked below `role`, which checking `role` also checks. */
export function getRolesImpliedBy(
  roleOptions: readonly MemberRoleMap[],
  role: MemberRole,
): MemberRole[] {
  const ranked = getAssignableRoleValues(roleOptions);
  const rank = ranked.indexOf(role);
  return rank === -1 ? [] : ranked.slice(rank + 1);
}

/**
 * Assignable roles locked by a selected higher role: they render checked and
 * can't be unchecked until every higher role is unchecked.
 */
export function getLockedRoleValues(
  roleOptions: readonly MemberRoleMap[],
  selectedRoleValues: readonly MemberRole[],
): Set<MemberRole> {
  const ranked = getAssignableRoleValues(roleOptions);
  const highestRank = ranked.findIndex((value) => selectedRoleValues.includes(value));
  return new Set(highestRank === -1 ? [] : ranked.slice(highestRank + 1));
}

/** The highest-ranked selected assignable role, if any. */
export function getHighestSelectedRole(
  roleOptions: readonly MemberRoleMap[],
  selectedRoleValues: readonly MemberRole[],
): MemberRoleMap | undefined {
  return roleOptions.find(
    ({ assignable, value }) => assignable !== false && selectedRoleValues.includes(value),
  );
}

/**
 * A product's roles plus the roles they imply, e.g. Viewer for a member who
 * holds Admin but not Viewer. Used to seed the edit trays so a held higher role
 * reads the same as one chosen in the picker: unchecking it leaves the lower
 * roles checked.
 */
export function withImpliedRoles(
  roles: readonly MemberRole[],
  roleOptions: readonly MemberRoleMap[],
): MemberRole[] {
  const implied = roles.flatMap((role) => getRolesImpliedBy(roleOptions, role));
  return [...new Set([...roles, ...implied])];
}
